"use client";
/* eslint-disable @typescript-eslint/no-explicit-any, react-hooks/set-state-in-effect, react-hooks/preserve-manual-memoization */

import { motion } from "framer-motion";
import { useEffect, useState, useMemo, useCallback } from "react";
import { pageVariants, cardVariants, slideFromBottomVariants, headerVariants } from "@/lib/animations";
import ProductDetailMenu, { type MenuItems } from "@/components/ui/ProductDetailMenu";
import ScrollProgressIndicator from "@/components/ui/ScrollProgressIndicator";
import ProductShareButton from "@/components/ui/ProductShareButton";
import ProductMiceServiceFeatures from "@/components/ui/ProductMiceServiceFeatures";
import ProductMiceWhyUs from "@/components/ui/ProductMiceWhyUs";
import ProcessTimeline from "@/components/ui/ProcessTimeline";
import ProductMiceReferences from "@/components/ui/ProductMiceReferences";
import RequestForm from "@/components/ui/RequestForm";
import Faqs from "@/components/sections/Faqs";
import { useProductMiceData } from "@/hooks/useProductMiceData";
import { useProductBreadcrumbs } from "@/hooks/useProductBreadcrumbs";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import type { FaqEntry } from "@/types/faq";
import type { PageUrlMap } from "@/types/blog";
import { sanitizeCmsHtml } from "@/lib/sanitize-cms-html";
import type { OverviewStatItem, OverviewHighlight } from "@/services/ProductMiceDataService";

interface ProductMiceDetailProps {
    product?: {
        id?: number;
        name?: string;
        type?: string;
        product_type?: string;
        description?: string;
        gallery?: Array<any>;
        metas?: Record<string, any>;
        fields?: Record<string, any>;
        categories?: Array<{
            id?: number;
            name?: string;
            slug?: string;
        }>;
    };
    language?: string;
    languageId?: number;
    translations?: Record<string, string>;
    menuItems?: MenuItems[];
    productFaqs?: FaqEntry[];
    requestForm?: {
        id?: number;
        slug?: string;
        title?: string;
    } | null;
    pageUrls?: PageUrlMap;
    contractsPageUrl?: string;
    defaultLanguage?: string;
    initialFormDetails?: any;
}

const sectionBlock =
    "mb-12 scroll-mt-20 sm:mb-16 sm:scroll-mt-24 lg:mb-24";
const sectionFollowing =
    "border-t border-gray-100/90 pt-8 sm:pt-12 lg:pt-16 mb-12 sm:mb-16 lg:mb-24 scroll-mt-20 sm:scroll-mt-24";

/**
 * Sayfa gövdesindeki gerçek sıra: Overview → Service Features → Process → Why Us → Additional → References → tab alanları → FAQ.
 * CMS’den gelen menü öğelerini bu sıraya göre düzenler (hash id’si üzerinden).
 */
function sortMiceMenuByPageOrder(
    items: MenuItems[] | undefined,
    tabSectionIdsInOrder: string[]
): MenuItems[] | undefined {
    if (!items?.length) return items;

    const tabRank = new Map<string, number>();
    tabSectionIdsInOrder.forEach((id, i) => {
        tabRank.set(id, 6 + i * 0.01);
    });

    const fixedRank: Record<string, number> = {
        overview: 0,
        features: 1,
        process: 2,
        "why-us": 3,
        "additional-info": 4,
        references: 5,
        faqs: 18,
    };

    const linkRank = (link: string): number => {
        const id = link.replace(/^#/, "").trim().split("?")[0];
        if (id in fixedRank) return fixedRank[id];
        if (tabRank.has(id)) return tabRank.get(id)!;
        return 99;
    };

    const sortedTop = [...items]
        .map((item, stackIdx) => ({ item, stackIdx }))
        .sort((a, b) => {
            const ra = linkRank(a.item.link);
            const rb = linkRank(b.item.link);
            if (ra !== rb) return ra - rb;
            return a.stackIdx - b.stackIdx;
        })
        .map(({ item }) => {
            if (item.isDropdown && item.children?.length) {
                const children = [...item.children]
                    .map((c, i) => ({ c, i }))
                    .sort((a, b) => {
                        const ra = linkRank(a.c.link);
                        const rb = linkRank(b.c.link);
                        if (ra !== rb) return ra - rb;
                        return a.i - b.i;
                    })
                    .map(({ c }) => c);
                return { ...item, children };
            }
            return item;
        });

    return sortedTop;
}

function MiceOverviewStatsGrid({ items }: { items: OverviewStatItem[] }) {
    if (!items.length) return null;
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {items.map((item, idx) => {
                const isHighlight = item.style === "highlight";
                return (
                    <div
                        key={`${item.value}-${item.label}-${idx}`}
                        className={[
                            "flex min-h-[120px] flex-col rounded-xl border bg-white/90 p-4 shadow-[0_1px_3px_rgba(15,23,42,0.06)] transition-shadow duration-200",
                            isHighlight
                                ? "border-alsero-first/35 bg-alsero-first/[0.06] shadow-[0_2px_8px_rgba(180,30,40,0.08)]"
                                : "border-gray-200/90 hover:shadow-[0_4px_14px_rgba(15,23,42,0.07)]",
                        ].join(" ")}
                    >
                        <p className="text-2xl font-bold tracking-tight text-alsero-dark-gray sm:text-[1.65rem]">
                            {item.value}
                        </p>
                        <p className="mt-1.5 text-sm font-medium text-alsero-text-tour">{item.label}</p>
                        {item.description ? (
                            <p className="mt-2 text-xs leading-relaxed text-gray-500 sm:text-[13px]">{item.description}</p>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}

function MiceOverviewHighlightBox({ h }: { h: OverviewHighlight }) {
    const hasBody = Boolean((h.title && h.title.trim()) || (h.textHtml && h.textHtml.trim()));
    if (!hasBody) return null;

    const hasCta = Boolean(h.ctaText?.trim() && h.ctaUrl?.trim());

    return (
        <div className="rounded-xl border border-gray-200/80 border-l-4 border-l-alsero-first bg-gradient-to-br from-gray-50/90 to-white p-5 shadow-sm sm:p-6">
            {h.badge?.trim() ? (
                <span className="mb-3 inline-block rounded-full border border-alsero-first/25 bg-alsero-first/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-alsero-first">
                    {h.badge.trim()}
                </span>
            ) : null}
            {h.title?.trim() ? (
                <h3 className="text-lg font-bold leading-snug text-alsero-dark-gray sm:text-xl">{h.title.trim()}</h3>
            ) : null}
            {h.textHtml ? (
                <div
                    className="mt-3 text-sm leading-relaxed text-alsero-text-tour prose prose-sm max-w-none [&_p]:mb-3 [&_p:last-child]:mb-0"
                    dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(h.textHtml) }}
                />
            ) : null}
            {hasCta ? (
                <div className="mt-5 border-t border-gray-200/80 pt-4">
                    <a
                        href={h.ctaUrl!}
                        className="inline-flex items-center gap-2 rounded-lg border border-alsero-first/30 bg-white px-4 py-2 text-sm font-semibold text-alsero-first shadow-sm transition-colors hover:bg-alsero-first/5"
                    >
                        {h.ctaText!.trim()}
                    </a>
                </div>
            ) : null}
        </div>
    );
}

export default function ProductMiceDetail({
    product,
    language = 'en',
    languageId,
    translations = {},
    menuItems,
    productFaqs = [],
    pageUrls,
    defaultLanguage,
    initialFormDetails,
}: ProductMiceDetailProps = {}) {
    const [isFavorite, setIsFavorite] = useState(false);

    const { getVariable } = useLanguageVariables();
    const currentLanguage = language || getVariable('language', 'en');

    const miceData = useProductMiceData(product, currentLanguage);
    const { breadcrumbs } = useProductBreadcrumbs(product, currentLanguage, true, pageUrls, defaultLanguage);

    const t = useCallback((key: string, fallback?: string): string => {
        return translations[key] || getVariable(key, fallback || key);
    }, [translations, getVariable]);

    useEffect(() => {
        if (!product?.id) return;
        const favorites = JSON.parse(localStorage.getItem('mice_favorites') || '[]');
        setIsFavorite(favorites.includes(product.id));
    }, [product?.id]);

    const toggleFavorite = useCallback(() => {
        if (!product?.id) return;
        const favorites = JSON.parse(localStorage.getItem('mice_favorites') || '[]');
        
        if (isFavorite) {
            const updated = favorites.filter((id: number) => id !== product.id);
            localStorage.setItem('mice_favorites', JSON.stringify(updated));
            setIsFavorite(false);
        } else {
            favorites.push(product.id);
            localStorage.setItem('mice_favorites', JSON.stringify(favorites));
            setIsFavorite(true);
        }
    }, [isFavorite, product?.id]);

    const hasOverviewSectionContent = useMemo(() => {
        const ov = miceData.overview?.trim();
        const asideStats = miceData.overviewStats.length > 0;
        const hl = miceData.overviewHighlight;
        const asideHighlight = Boolean(
            (hl.title && hl.title.trim()) || (hl.textHtml && hl.textHtml.trim())
        );
        return Boolean(ov || asideStats || asideHighlight);
    }, [miceData.overview, miceData.overviewStats, miceData.overviewHighlight]);

    const hasProcessSection = useMemo(() => {
        const desc = miceData.processDesc?.trim();
        const steps = miceData.processSteps.length > 0;
        return Boolean(desc || steps);
    }, [miceData.processDesc, miceData.processSteps]);

    /** Tab bölümlerinin DOM sırası (menu sıralaması için) */
    const tabSectionIdsInOrder = useMemo(
        () => miceData.tabFields.map((g) => g.sectionId),
        [miceData.tabFields]
    );

    /** CMS menüsü — sayfa içerik sırasıyla hizalı */
    const orderedMenuItems = useMemo(
        () => sortMiceMenuByPageOrder(menuItems, tabSectionIdsInOrder),
        [menuItems, tabSectionIdsInOrder]
    );

    const scrollSections = useMemo(() => {
        const sections: Array<{ id: string; label: string }> = [];
        const sectionIds = new Set<string>();

        // Sayfa gövdesi ile aynı sıra: Overview → Service Features → Process → Why Us → Additional → References → Tab alanları → FAQ
        if (hasOverviewSectionContent && !sectionIds.has("overview")) {
            sectionIds.add("overview");
            sections.push({ id: "overview", label: t("overview", "Overview") });
        }

        if (miceData.serviceFeatures.included.length > 0 && !sectionIds.has("features")) {
            sectionIds.add("features");
            sections.push({ id: "features", label: t("service_features", "Service Features") });
        }

        if (hasProcessSection && !sectionIds.has("process")) {
            sectionIds.add("process");
            sections.push({ id: "process", label: t("process", "Process") });
        }

        if (miceData.whyUsItems.length > 0 && !sectionIds.has("why-us")) {
            sectionIds.add("why-us");
            sections.push({ id: "why-us", label: t("why_us", "Why Us") });
        }

        if (miceData.additionalInfo && !sectionIds.has("additional-info")) {
            sectionIds.add("additional-info");
            sections.push({
                id: "additional-info",
                label: t("additional_information", "Additional Information"),
            });
        }

        if (miceData.references.length > 0 && !sectionIds.has("references")) {
            sectionIds.add("references");
            sections.push({ id: "references", label: t("references", "References") });
        }

        miceData.tabFields.forEach((tabGroup) => {
            if (!sectionIds.has(tabGroup.sectionId)) {
                sectionIds.add(tabGroup.sectionId);
                const isCancellation =
                    /cancellation|iptal/i.test(tabGroup.tabName) ||
                    /cancellation|iptal/i.test(tabGroup.sectionId);
                sections.push({
                    id: tabGroup.sectionId,
                    label: isCancellation
                        ? t("cancellation_conditions", "Cancellation Conditions")
                        : tabGroup.tabName,
                });
            }
        });

        if (productFaqs && productFaqs.length > 0 && !sectionIds.has("faqs")) {
            sectionIds.add("faqs");
            sections.push({ id: "faqs", label: t("faqs", "FAQs") });
        }

        return sections;
    }, [
        miceData.tabFields,
        hasOverviewSectionContent,
        hasProcessSection,
        miceData.serviceFeatures,
        miceData.whyUsItems,
        miceData.references,
        miceData.additionalInfo,
        productFaqs,
        t,
    ]);

    return (
        <motion.div 
            className="relative z-10 mx-auto min-h-screen w-full max-w-full min-w-0 px-3 py-8 sm:px-4 sm:py-12 lg:w-9/12 lg:px-4 lg:py-16" 
            initial="hidden" 
            animate="visible" 
            variants={pageVariants}>

            <motion.div 
                className="mt-6 grid grid-cols-12 gap-6 sm:mt-8 sm:gap-8" 
                initial="hidden" 
                animate="visible" 
                variants={pageVariants}
            >
                <div className="col-span-12 min-w-0 lg:col-span-8">
                <div className="sticky top-0 lg:top-5 z-[60] mb-4 flex flex-row items-center gap-2 bg-white/95 py-2 backdrop-blur-sm sm:gap-3 sm:bg-transparent sm:py-0 sm:backdrop-blur-none">
                        <div className="flex-1 min-w-0">
                            <ProductDetailMenu links={orderedMenuItems} />
                        </div>
                        <ScrollProgressIndicator sections={scrollSections} />
                    </div>
                    {/* Hero Section */}
                    <motion.section 
                        className="mb-10 sm:mb-12 lg:mb-14"
                        variants={slideFromBottomVariants}
                    >
                        <motion.div
                            variants={cardVariants}
                            className="rounded-2xl border border-gray-100/90 bg-gradient-to-b from-gray-50/60 to-white p-4 shadow-sm sm:p-6 md:p-8"
                        >
                            <h1 className="text-balance break-words text-xl font-bold leading-tight text-alsero-dark-gray sm:text-2xl lg:text-3xl mb-3 sm:mb-4">
                                {product?.name || t('mice_detail', 'M.I.C.E Service')}
                            </h1>
                            {breadcrumbs && breadcrumbs.length > 0 && (
                                <nav className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-alsero-text-tour sm:mb-6 sm:text-sm" aria-label="Breadcrumb">
                                    {breadcrumbs.map((crumb, index) => (
                                        <span key={index} className="inline-flex min-w-0 items-center gap-2">
                                            {index > 0 && <span className="text-gray-300" aria-hidden>/</span>}
                                            {crumb.link ? (
                                                <a href={crumb.link} className="min-w-0 truncate hover:text-alsero-first transition-colors">
                                                    {crumb.title}
                                                </a>
                                            ) : (
                                                <span className="min-w-0 break-words">{crumb.title}</span>
                                            )}
                                        </span>
                                    ))}
                                </nav>
                            )}
                            
                            {/* Quick Info - Service Type & Location */}
                            <div className="mt-3 flex flex-wrap gap-2 sm:mt-4 sm:gap-4">
                                {miceData.serviceType && (
                                    <span className="rounded-full bg-alsero-first/10 px-3 py-1.5 text-xs font-semibold text-alsero-first sm:px-4 sm:py-2 sm:text-sm">
                                        {t(`mice.service_type.${miceData.serviceType}`, miceData.serviceType)}
                                    </span>
                                )}
                                {miceData.location.length > 0 && (
                                    <div className="flex min-w-0 flex-1 flex-wrap gap-2">
                                        {miceData.location.map((loc, idx) => (
                                            <span key={idx} className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-alsero-dark-gray sm:px-3 sm:text-sm">
                                                {loc}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.section>

                    {/* Overview — sol metin, sağ istatistik + vurgu */}
                    {hasOverviewSectionContent && (
                        <motion.section
                            id="overview"
                            className={sectionBlock}
                            variants={slideFromBottomVariants}
                            aria-labelledby="overview-heading"
                        >
                            <motion.h2
                                id="overview-heading"
                                className="mb-6 text-xl font-bold text-alsero-dark-gray sm:mb-8 lg:mb-10 lg:text-3xl"
                                variants={headerVariants}
                            >
                                {t("overview", "Overview")}
                            </motion.h2>
                            <div className="grid grid-cols-1 gap-6 sm:gap-8 lg:grid-cols-12 lg:gap-8 lg:gap-y-10">
                                {miceData.overview?.trim() ? (
                                    <motion.div
                                        className={
                                            miceData.overviewStats.length > 0 ||
                                            Boolean(
                                                (miceData.overviewHighlight.title?.trim() ||
                                                    miceData.overviewHighlight.textHtml?.trim())
                                            )
                                                ? "lg:col-span-7"
                                                : "lg:col-span-12"
                                        }
                                        variants={cardVariants}
                                    >
                                        <div className="rounded-xl border border-gray-200/95 bg-white p-4 shadow-sm sm:p-6 md:p-8">
                                            <div
                                                className="max-w-full min-w-0 text-alsero-text-tour prose prose-base max-w-none leading-[1.65] sm:prose-lg sm:leading-[1.7] [&_li]:my-1.5 [&_p]:mb-4 [&_p:last-child]:mb-0 [&_ul]:my-3"
                                                dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(miceData.overview) }}
                                            />
                                        </div>
                                    </motion.div>
                                ) : null}

                                {miceData.overviewStats.length > 0 ||
                                (miceData.overviewHighlight.title?.trim() ||
                                    miceData.overviewHighlight.textHtml?.trim()) ? (
                                    <motion.div
                                        className={
                                            miceData.overview?.trim()
                                                ? "lg:col-span-5"
                                                : "lg:col-span-12 lg:max-w-2xl"
                                        }
                                        variants={cardVariants}
                                    >
                                        <div className="flex flex-col gap-7 lg:gap-8">
                                            <MiceOverviewStatsGrid items={miceData.overviewStats} />
                                            <MiceOverviewHighlightBox h={miceData.overviewHighlight} />
                                        </div>
                                    </motion.div>
                                ) : null}
                            </div>
                        </motion.section>
                    )}

                    {/* Service Features Section */}
                    {miceData.serviceFeatures.included.length > 0 && (
                        <motion.section
                            id="features"
                            className={sectionFollowing}
                            variants={slideFromBottomVariants}
                            aria-labelledby="features-heading"
                        >
                            <motion.h2
                                id="features-heading"
                                className="mb-6 text-xl font-bold text-alsero-dark-gray sm:mb-8 lg:mb-10 lg:text-3xl"
                                variants={headerVariants}
                            >
                                {t("service_features", "Service Features")}
                            </motion.h2>
                            <motion.div variants={cardVariants}>
                                <ProductMiceServiceFeatures items={miceData.serviceFeatures.included} />
                            </motion.div>
                        </motion.section>
                    )}
                    {/* Process — process_desc (giriş) + process (services_repeater → timeline) */}
                    {hasProcessSection && (
                        <motion.section
                            id="process"
                            className={sectionFollowing}
                            variants={slideFromBottomVariants}
                            aria-labelledby="process-heading"
                        >
                            <div className="flex flex-col gap-4 sm:gap-6 lg:flex-row lg:items-start lg:justify-between lg:gap-10">
                                <motion.h2
                                    id="process-heading"
                                    className="shrink-0 text-xl font-bold text-alsero-dark-gray lg:text-3xl"
                                    variants={headerVariants}
                                >
                                    {t("process", "Process")}
                                </motion.h2>
                                {miceData.processDesc?.trim() ? (
                                    <div
                                        className="min-w-0 max-w-full flex-1 text-base leading-relaxed text-alsero-text-tour prose prose-base sm:prose-lg md:text-lg [&_p]:mb-3 [&_p:last-child]:mb-0"
                                        dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(miceData.processDesc) }}
                                    />
                                ) : null}
                            </div>
                            {miceData.processSteps.length > 0 ? (
                                <motion.div className="mt-8 lg:mt-10" variants={cardVariants}>
                                    <ProcessTimeline
                                        hideTitle
                                        title={t("process", "Process")}
                                        steps={miceData.processSteps}
                                    />
                                </motion.div>
                            ) : null}
                        </motion.section>
                    )}

                    {/* Why Us — repeater kartlar */}
                    {miceData.whyUsItems.length > 0 && (
                        <motion.section
                            id="why-us"
                            className={sectionFollowing}
                            variants={slideFromBottomVariants}
                            aria-labelledby="why-us-heading"
                        >
                            <motion.h2
                                id="why-us-heading"
                                className="mb-6 text-xl font-bold text-alsero-dark-gray sm:mb-8 lg:mb-10 lg:text-3xl"
                                variants={headerVariants}
                            >
                                {t("why_us", "Why Us")}
                            </motion.h2>
                            <motion.div variants={cardVariants}>
                                <ProductMiceWhyUs items={miceData.whyUsItems} />
                            </motion.div>
                        </motion.section>
                    )}

                    {/* Additional Info Section */}
                    {miceData.additionalInfo && (
                        <motion.section
                            id="additional-info"
                            className={sectionFollowing}
                            variants={slideFromBottomVariants}
                            aria-labelledby="additional-info-heading"
                        >
                            <motion.h2
                                id="additional-info-heading"
                                className="mb-6 text-xl font-bold text-alsero-dark-gray sm:mb-8 lg:mb-10 lg:text-3xl"
                                variants={headerVariants}
                            >
                                {t("additional_information", "Additional Information")}
                            </motion.h2>
                            <motion.div
                                className="rounded-xl border border-gray-200/90 bg-white p-4 shadow-sm sm:p-6 md:p-8"
                                variants={cardVariants}
                            >
                                <div
                                    className="min-w-0 max-w-full text-alsero-text-tour leading-relaxed prose prose-base max-w-none sm:prose-lg"
                                    dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(miceData.additionalInfo) }}
                                />
                            </motion.div>
                        </motion.section>
                    )}

                    {/* References Section */}
                    {miceData.references.length > 0 && (
                        <motion.section
                            id="references"
                            className={sectionFollowing}
                            variants={slideFromBottomVariants}
                            aria-labelledby="references-heading"
                        >
                            <motion.h2
                                id="references-heading"
                                className="mb-6 text-xl font-bold text-alsero-dark-gray sm:mb-8 lg:mb-10 lg:text-3xl"
                                variants={headerVariants}
                            >
                                {t("references", "References")}
                            </motion.h2>
                            <ProductMiceReferences 
                                references={miceData.references}
                                language={currentLanguage}
                            />
                        </motion.section>
                    )}

                    {/* Tab Fields */}
                    {miceData.tabFields.map((tabGroup) => {
                        const isCancellationSection = /cancellation|iptal/i.test(tabGroup.tabName) || /cancellation|iptal/i.test(tabGroup.sectionId);
                        return (
                            <motion.section
                                key={tabGroup.sectionId}
                                id={tabGroup.sectionId}
                                className={sectionFollowing}
                                variants={slideFromBottomVariants}
                                aria-labelledby={`${tabGroup.sectionId}-heading`}
                            >
                                <motion.h2
                                    id={`${tabGroup.sectionId}-heading`}
                                    className="mb-6 text-xl font-bold text-alsero-dark-gray sm:mb-8 lg:mb-10 lg:text-3xl"
                                    variants={headerVariants}
                                >
                                    {isCancellationSection ? t('cancellation_conditions', 'Cancellation Conditions') : tabGroup.tabName}
                                </motion.h2>
                                {tabGroup.fields.map((field) => (
                                    <motion.div
                                        key={field.fieldKey}
                                        className="mb-8"
                                        variants={cardVariants}
                                    >
                                        {tabGroup.fields.length > 1 && (
                                            <h3 className="mb-3 text-lg font-semibold text-alsero-dark-gray sm:mb-4 sm:text-xl">
                                                {field.fieldTitle}
                                            </h3>
                                        )}
                                        {isCancellationSection ? (
                                            <ul className="list-disc space-y-2 pl-5 text-alsero-text-tour leading-relaxed prose prose-base max-w-none sm:pl-6 sm:prose-lg">
                                                <li>{t('cancellation_condition_45_days', 'For cancellations made 45 days before the tour, 100% refund. (Except per person 175 € for register, bank commission, and domestic flight ticket cost)')}</li>
                                                <li>{t('cancellation_condition_44_30_days', 'For cancellations made 44 - 30 days before the tour, 50% refund. (Except for the Bank Commission)')}</li>
                                                <li>{t('cancellation_condition_29_15_days', 'For cancellations made 29 - 15 days before the tour, 20% refund.')}</li>
                                                <li>{t('cancellation_condition_14_days_no_refund', 'No refund cancellation 14 days before the tour. A 100-percent Penalty will be applied.')}</li>
                                            </ul>
                                        ) : (
                                            <div
                                                className="min-w-0 max-w-full text-alsero-text-tour leading-relaxed prose prose-base max-w-none sm:prose-lg [&_img]:max-w-full [&_img]:h-auto"
                                                dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(field.content) }}
                                            />
                                        )}
                                    </motion.div>
                                ))}
                            </motion.section>
                        );
                    })}

                    {/* FAQ Section */}
                    {productFaqs && productFaqs.length > 0 && (
                        <motion.section
                            id="faqs"
                            className={sectionFollowing}
                            variants={slideFromBottomVariants}
                            aria-labelledby="faqs-heading"
                        >
                            <Faqs language={currentLanguage} generalFaqs={productFaqs} />
                        </motion.section>
                    )}
                </div>

                {/* Sidebar - Request Form */}
                <div className="col-span-12 min-w-0 lg:col-span-4">
                    <div className="lg:sticky lg:top-24">
                        <motion.div
                            className="overflow-hidden rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6 max-w-full"
                            variants={cardVariants}
                        >
                            <h3 className="mb-3 text-lg font-bold text-alsero-dark-gray sm:mb-4 sm:text-xl">
                                {t('request_information', 'Request Information')}
                            </h3>
                            {(miceData.ctaRequestHeadline || miceData.ctaRequestSubtext) && (
                                <div className="mb-5 border-b border-gray-100 pb-5 sm:mb-6 sm:pb-6">
                                    {miceData.ctaRequestHeadline ? (
                                        <p className="mb-2 text-base font-semibold text-alsero-dark-gray sm:text-lg">
                                            {miceData.ctaRequestHeadline}
                                        </p>
                                    ) : null}
                                    {miceData.ctaRequestSubtext ? (
                                        <div
                                            className="text-sm text-alsero-text-tour prose prose-sm max-w-none leading-relaxed"
                                            dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(miceData.ctaRequestSubtext) }}
                                        />
                                    ) : null}
                                </div>
                            )}
                            {product?.id ? (
                                <RequestForm
                                    productId={product.id}
                                    productType={product?.type || product?.product_type || 'mice'}
                                    productName={product?.name}
                                    language={currentLanguage}
                                    languageId={languageId}
                                    translations={translations}
                                    initialFormDetails={initialFormDetails}
                                    className="!p-0"
                                />
                            ) : (
                                <p className="text-alsero-text-tour text-sm">
                                    {t('request_form_coming_soon', 'Request form will be available here.')}
                                </p>
                            )}
                        </motion.div>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
}
